import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import styles from './DraggableFilterButtons.module.scss';
import { hide, setupTransitionInTimeline } from './DraggableFilterButtons.transitions';
import type {
  DraggableFilterButtonsProps,
  DraggableFilterButtonsRef,
} from './DraggableFilterButtons.data';
import { useScrollTransition } from 'util/react-transition-component';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import { useDraggable } from '@/hooks/useDraggable';

export default function DraggableFilterButtons({
  filters,
  colorVariation,
  disabled,
  activeFilter,
  type,
  onFilterClick,
  ref,
  ...props
}: DraggableFilterButtonsProps & { ref?: Ref<DraggableFilterButtonsRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const containerRef = useRef<HTMLDivElement>(null);

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  useEffect(() => {
    hide({ elementRef }, disabled);
  }, [disabled, elementRef]);

  useDraggable(
    containerRef,
    {
      type: 'x',
      edgeResistance: 1,
      lockAxis: true,
      inertia: true,
      cursor: 'grab',
    },
    {
      bounds: elementRef,
    },
  );

  return (
    <div ref={elementRef} className={classNames(styles.draggableFilterButtons, props.className)}>
      <div ref={containerRef} className={classNames(styles.buttonsHolder)}>
        {filters.map((item, index) => (
          <PrimaryButton
            key={item.filter}
            className={classNames(styles.button)}
            label={item.filter}
            icon={item.icon}
            disabled={disabled}
            isSelected={item.filter === activeFilter}
            onClick={() => onFilterClick(item.filter, type)}
            disableTriggerInOnScroll={false}
            colorVariation={colorVariation}
            transitionInDelay={(index % filters.length) * 0.1}
          />
        ))}
      </div>
    </div>
  );
}
